import {
  FP_SHOW_WHEN_CONDITIONS,
  isDateOnlyExpired,
  parseShowWhenValue,
  type FpShowWhenCondition,
  type FpShowWhenConjunct,
  type PublicQuote,
} from "@forgepoint/shared";
import { sectionMemberLines } from "./FpLines";
import { resolveLineField } from "./lineFields";
import type { LineScopeValue } from "./lineScopeContext";
import { quoteFieldLabel, resolveQuoteField } from "./quoteFields";

/** Named-condition resolvers keyed by the `data-fp-show-when` vocabulary. */
const CONDITION_RESOLVERS: Record<
  FpShowWhenCondition,
  (quote: PublicQuote, scope: LineScopeValue | null) => boolean
> = {
  "has-multiple-breaks": (quote, scope) =>
    scope?.line.type === "ITEM"
      ? scope.line.quantityBreaks.length > 1
      : quote.lines.some(
          (line) => line.type === "ITEM" && line.quantityBreaks.length > 1,
        ),
  "has-items": (quote, scope) =>
    (scope?.section
      ? sectionMemberLines(quote.lines, scope.section)
      : quote.lines
    ).some((line) => line.type === "ITEM"),
  "discounts-change-subtotal": (quote) =>
    quote.subtotalWithDiscounts !== null &&
    quote.subtotalWithDiscounts !== quote.subtotal,
  "has-terms": (quote) =>
    isFilledIn(quote.deliveryTerms) || isFilledIn(quote.paymentTerms),
  signed: (quote) => quote.isSigned && quote.signature !== null,
  "show-totals": (quote) => quote.showTotals,
  "show-total-weight": (quote) => quote.showTotalWeight,
  expired: (quote) => isDateOnlyExpired(quote.expirationDate),
};

/**
 * Whether a nullable authored string carries content.
 * @param value - The stored value, or null when unset
 * @returns True when the value has non-whitespace content
 */
function isFilledIn(value: string | null): boolean {
  return value !== null && value.trim() !== "";
}

/**
 * Whether an element carrying `data-fp-show-when` renders: every `&&`-joined
 * conjunct holds, each being a named condition or a bound field resolving
 * non-empty in the current scope. A leading `!` inverts its own conjunct.
 * @param quote - The public quote backing the document
 * @param scope - The enclosing repeater scope, or null at quote scope
 * @param value - The `data-fp-show-when` value
 * @returns True when the element should render
 */
export function shouldShowWhen(
  quote: PublicQuote,
  scope: LineScopeValue | null,
  value: string,
): boolean {
  return parseShowWhenValue(value).every(({ negated, condition }) => {
    const resolved = resolveCondition(quote, scope, condition);
    return negated ? !resolved : resolved;
  });
}

/**
 * Resolves one bare condition (never negated): a named condition, or a bound
 * field being non-empty.
 * @param quote - The public quote backing the document
 * @param scope - The enclosing repeater scope, or null at quote scope
 * @param condition - The condition with any negation prefix already stripped
 * @returns True when the condition holds
 */
function resolveCondition(
  quote: PublicQuote,
  scope: LineScopeValue | null,
  condition: string,
): boolean {
  if (isShowWhenCondition(condition)) {
    return CONDITION_RESOLVERS[condition](quote, scope);
  }
  if (scope) {
    const resolved = resolveLineField(quote, scope, condition);
    if (resolved !== null) {
      return resolved.value !== "";
    }
  }
  const value = resolveQuoteField(quote, condition);
  return value !== null && value !== "";
}

/**
 * The human-readable name of a `data-fp-show-when` value, for the inspector.
 * @param value - The `data-fp-show-when` value
 * @returns The conditions' display label, conjuncts joined by "and"
 */
export function showWhenLabel(value: string): string {
  return parseShowWhenValue(value).map(conjunctLabel).join(" and ");
}

/**
 * The human-readable name of one conjunct.
 * @param conjunct - The parsed condition and its negation flag
 * @returns The conjunct's display label
 */
function conjunctLabel({ negated, condition }: FpShowWhenConjunct): string {
  const label = isShowWhenCondition(condition)
    ? SHOW_WHEN_CONDITION_LABELS[condition]
    : `${quoteFieldLabel(condition) ?? condition} is filled in`;
  return negated ? `Not: ${label}` : label;
}

/** End-user labels for the named conditions, shown in the inspector. */
export const SHOW_WHEN_CONDITION_LABELS: Record<FpShowWhenCondition, string> = {
  "has-multiple-breaks": "Line has quantity breaks",
  "has-items": "Quote has items",
  "discounts-change-subtotal": "Discounts change the subtotal",
  "has-terms": "Quote has delivery or payment terms",
  signed: "Quote is signed",
  "show-totals": "Totals are shown",
  "show-total-weight": "Total weight is shown",
  expired: "Quote has expired",
};

/**
 * Whether a raw value is one of the named show-when conditions.
 * @param condition - The raw `data-fp-show-when` value
 * @returns True when the value is a known named condition
 */
function isShowWhenCondition(
  condition: string,
): condition is FpShowWhenCondition {
  return FP_SHOW_WHEN_CONDITIONS.some((candidate) => candidate === condition);
}
